import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { StockCategory } from '../../../models/stockCategory';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LookupTableSearchBoxComponent } from '../../shared/lookup-table-search-box/lookup-table-search-box.component';
import { LookupTableSearchFilter } from '../../../models/lookupTableSearchFilter';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { LutService } from '../../../services/lut.service';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-stock-category-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    LookupTableSearchBoxComponent,
    NgxSpinnerModule
  ],
  templateUrl: './stock-category-list.component.html',
  styleUrl: './stock-category-list.component.scss'
})
export class StockCategoryListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  stockCategories = signal<StockCategory[]>([]);
  allStockCategories: StockCategory[] = [];
  rightItem: StockCategory | undefined = undefined;
  matchedItems: StockCategory[] | undefined = undefined;
  isSearchVisible = false;
  searchFilter: LookupTableSearchFilter = {};

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadStockCategories();
  }

  loadStockCategories(): void {
    this.spinnerService.show();
    this.lutService.getStockCategories().subscribe({
      next: (data) => {
        // Sort alphabetically by name before setting
        const sortedData = this.sortStockCategories(data);
        this.allStockCategories = sortedData;
        this.stockCategories.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading stock categories:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortStockCategories(stockCategories: StockCategory[]): StockCategory[] {
    return [...stockCategories].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateStockCategory(): void {
    this.lookupTable.startAdd();
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  getAppliedFiltersCount(): number {
    return Object.values(this.searchFilter).filter(value => value !== undefined && value !== null && value !== '').length;
  }

  onItemSelected(stockCategory: StockCategory): void {
  }


  onEditStockCategory(stockCategory: StockCategory): void {
  }

  onDeleteStockCategory(stockCategory: StockCategory): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(stockCategory.name)
      .subscribe(result => {
        if (result) {
          // Store original lists for rollback in case of error
          const originalStockCategories = this.stockCategories();
          const originalAllStockCategories = this.allStockCategories;

          // 1. Optimistically remove from local state immediately
          this.stockCategories.update(stockCategories => stockCategories.filter(sc => sc.id !== stockCategory.id));
          this.allStockCategories = this.allStockCategories.filter(sc => sc.id !== stockCategory.id);

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteStockCategory(stockCategory.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Stock category deleted successfully');
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting stock category:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original lists
              this.stockCategories.set(originalStockCategories);
              this.allStockCategories = originalAllStockCategories;
              this.toast.error(`<strong>Failed to delete Stock Category:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: StockCategory, newName: string} | {item: StockCategory, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };
    const originalAllStockCategories = this.allStockCategories;

    // Prepare updated item
    let updatedItem: StockCategory;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.stockCategories.update(stockCategories => {
      const updated = stockCategories.map(sc => sc.id === event.item.id ? updatedItem : sc);
      return this.sortStockCategories(updated);
    });
    this.allStockCategories = this.sortStockCategories(
      this.allStockCategories.map(sc => sc.id === event.item.id ? updatedItem : sc)
    );

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateStockCategory(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Stock category updated successfully');
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.stockCategories.update(stockCategories => {
          const updated = stockCategories.map(sc => sc.id === event.item.id ? response : sc);
          return this.sortStockCategories(updated);
        });
        this.allStockCategories = this.sortStockCategories(
          this.allStockCategories.map(sc => sc.id === event.item.id ? response : sc)
        );
      },
      error: (error) => {
        console.error('Error updating stock category:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.stockCategories.update(stockCategories =>
          stockCategories.map(sc => sc.id === event.item.id ? originalItem : sc)
        );
        this.allStockCategories = originalAllStockCategories;
        this.toast.error(`<strong>Failed to update Stock Category:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newStockCategory: StockCategory = { id: tempId, name: event['name'], systemReserved: false };

    // 1. Optimistically add to local state immediately and sort
    this.stockCategories.update(stockCategories => this.sortStockCategories([...stockCategories, newStockCategory]));
    this.allStockCategories = this.sortStockCategories([...this.allStockCategories, newStockCategory]);

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createStockCategory({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Stock category created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.stockCategories.update(stockCategories => {
          const updated = stockCategories.map(sc => sc.id === tempId ? response : sc);
          return this.sortStockCategories(updated);
        });
        this.allStockCategories = this.sortStockCategories(
          this.allStockCategories.map(sc => sc.id === tempId ? response : sc)
        );
      },
      error: (error) => {
        console.error('Error creating stock category:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.stockCategories.update(stockCategories =>
          stockCategories.filter(sc => sc.id !== tempId)
        );
        this.allStockCategories = this.allStockCategories.filter(sc => sc.id !== tempId);
        this.toast.error(`<strong>Failed to create Stock Category:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onStockCategoryCorrected(): void {
    // Validate that we have the required data
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select items to merge');
      return;
    }

    // Prepare the bulk merge request
    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    // Show confirmation dialog
    const itemsToMerge = this.matchedItems.map(item => item.name).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.name}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        // Store original list for rollback in case of error
        const originalStockCategories = this.stockCategories();

        // 1. Optimistically remove merged items from local state
        this.stockCategories.update(stockCategories =>
          stockCategories.filter(sc => !bulkMergeRequest.leftIds.includes(sc.id))
        );

        // Clear the selection state
        this.rightItem = undefined;
        this.matchedItems = undefined;

        // 2. Call backend API
        this.spinnerService.show();
        this.lutService.mergeStockCategoriesBulk(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Stock categories merged successfully');
            // 3. On success: Clear the lookup table's internal matching state and reload the list
            this.lookupTable.clearMatchingState();
            this.loadStockCategories();
          },
          error: (error) => {
            console.error('Error merging stock categories:', error);
            this.spinnerService.hide();
            // 4. On error: Restore original list
            this.stockCategories.set(originalStockCategories);
            // Restore selection state for retry
            this.rightItem = originalStockCategories.find(sc => sc.id === bulkMergeRequest.rightId);
            this.matchedItems = originalStockCategories.filter(sc => bulkMergeRequest.leftIds.includes(sc.id));
            this.toast.error(`<strong>Failed to merge Stock Categories:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }

  onReadyToMatch(event: {rightItem?: StockCategory, matchedItems?: StockCategory[]}): void {
    this.rightItem = event.rightItem;
    this.matchedItems = event.matchedItems;
  }

  onSearchFilterChange(searchFilter: LookupTableSearchFilter): void {
    this.searchFilter = searchFilter;
    this.spinnerService.show();
    setTimeout(() => {
      if (searchFilter.name) {
        const filtered = this.allStockCategories.filter(item =>
          item.name.toLowerCase().includes(searchFilter.name?.toLowerCase() ?? '')
        );
        this.stockCategories.set(filtered);
      } else {
        this.stockCategories.set(this.allStockCategories);
      }
      this.spinnerService.hide();
    }, 1500);
  }
}
